import { partition } from "@banjoanton/utils";
import type { ChessPlayerAnalysis } from "../models/chess-game-model";
import { Engine, EngineModel } from "../models/engine-model";
import type { MoveRating } from "../models/move-rating-model";
import type { ChessPositionAnalysis } from "../models/position-model";
import type { Move } from "../models/move-model";

export const shouldOverwriteAnalysis = (
    previousAnalysis: ChessPositionAnalysis | undefined,
    newEngineModel: EngineModel,
    newDepth: number
): boolean => {
    if (!previousAnalysis) {
        return true;
    }

    const previousEngineModel =
        previousAnalysis.engineModel ?? EngineModel.Stockfish16; // default to lowest
    const previousDepth = previousAnalysis.depth ?? 0;

    const isSameEngine = previousEngineModel === newEngineModel;
    const hasBetterDepth = newDepth > previousDepth;
    const hasBetterEngine = Engine.compare(newEngineModel, previousEngineModel);

    if (isSameEngine && hasBetterDepth) {
        return true;
    }
    if (hasBetterEngine) {
        return true;
    }

    return false;
};

const GOOD_MOVE_RATINGS: MoveRating[] = [
    "best",
    "excellent",
    "good",
    "brilliant",
    "great",
];

const getAccuracyForOneSide = (moves: Move[]): number | undefined => {
    const movesWithoutOpening = moves.filter(
        (m) => m.analysis?.rating !== "book"
    );

    if (movesWithoutOpening.length === 0) {
        return undefined;
    }

    const calculate = (moves: Move[]) => {
        const goodMoves = moves.filter((m) =>
            m.analysis ? GOOD_MOVE_RATINGS.includes(m.analysis.rating) : false
        ).length;

        return Number((goodMoves / moves.length).toFixed(3));
    };

    return calculate(moves);
};

type PlayerAnalysisResult = {
    white: ChessPlayerAnalysis;
    black: ChessPlayerAnalysis;
};

const getPlayerAnalysis = (moves: Move[]): PlayerAnalysisResult => {
    const movesWithoutBook = moves.filter((m) => m.analysis?.rating !== "book");

    const [blackMoves, whiteMoves] = partition(
        movesWithoutBook,
        (m) => m.color === "b"
    );

    const getMoveRatingCount = (moves: Move[], rating: MoveRating) =>
        moves.filter((m) => m.analysis?.rating === rating).length;

    const getAnalysis = (moves: Move[]): ChessPlayerAnalysis => {
        const totalCentipawnLoss = moves.reduce(
            (acc, m) => acc + (m.analysis?.centipawnLoss || 0),
            0
        );
        return {
            best: getMoveRatingCount(moves, "best"),
            excellent: getMoveRatingCount(moves, "excellent"),
            good: getMoveRatingCount(moves, "good"),
            inaccuracy: getMoveRatingCount(moves, "inaccuracy"),
            mistake: getMoveRatingCount(moves, "mistake"),
            blunder: getMoveRatingCount(moves, "blunder"),
            brilliant: getMoveRatingCount(moves, "brilliant"),
            great: getMoveRatingCount(moves, "great"),
            miss: getMoveRatingCount(moves, "miss"),
            averageCentipawnLoss: Number(
                (totalCentipawnLoss / moves.length).toFixed(2)
            ),
            accuracy: getAccuracyForOneSide(moves) ?? 0,
        };
    };

    return {
        white: getAnalysis(whiteMoves),
        black: getAnalysis(blackMoves),
    };
};

export const AnalysisUtil = { shouldOverwriteAnalysis, getPlayerAnalysis };
